<!-- CampaignSchedule.vue - Versão Simplificada com Limite de 365 -->
<script setup>
import { ref, computed, watch, onMounted, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { onBeforeRouteLeave, useRouter, useRoute } from 'vue-router';
import Input from 'dashboard/components-next/input/Input.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import PageHeader from 'dashboard/routes/dashboard/settings/SettingsSubPageHeader.vue';
import { useScheduling } from 'dashboard/composables/useScheduling';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from 'dashboard/components-shadcn/new-york/ui/tabs';
import { ArrowLeft, ArrowRight, Sparkles, Check } from 'lucide-vue-next';

const props = defineProps({
  templateName: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();

const {
  schedulingState,
  v$,
  currentDate,
  generateScheduledDates,
  scheduledDatesPreview,
  scheduleCount,
  isWithinLimit,
  MAX_SCHEDULES,
  resetState,
} = useScheduling();

const campaignData = computed(
  () => store.getters['campaigns/getWhatsappNormalCampaignData']
);
const isLoadingFromStore = ref(false);
const hasLoadedInitialData = ref(false);

const tabs = [
  {
    id: 'configure',
    label: 'Configurar Agendamento',
    shortLabel: 'Configurar',
    icon: '⚙️',
  },
  {
    id: 'preview',
    label: 'Visualizar Agenda',
    shortLabel: 'Visualizar',
    icon: '📅',
  },
];

const activeTab = ref('configure');
const recurrenceCombo = ref(null);

onBeforeRouteLeave((to, from, next) => {
  if (
    to.name === 'campaigns_whatsapp_app_home' ||
    !to.name?.includes('campaigns_whatsapp_app')
  ) {
    store.dispatch('campaigns/clearWhatsappNormalCampaignData');
  }
  next();
});

const formErrors = computed(() => ({
  startDate: v$.value.startDate.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.DATE_BEGIN_ERROR')
    : '',
  endDate: v$.value.endDate.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.DATE_END_ERROR')
    : '',
  fromTime: v$.value.fromTime.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.FROM_TIME_ERROR')
    : '',
  selectedDays: v$.value.selectedDays.$error
    ? t('CAMPAIGN.SMS.CREATE.FORM.SCHEDULING.SELECTED_DAYS_ERROR')
    : '',
}));

const showPreview = computed(() => {
  const hasBasicData =
    schedulingState.startDate &&
    schedulingState.endDate &&
    schedulingState.fromTime &&
    schedulingState.recurrenceType &&
    (schedulingState.recurrenceType !== 'custom' ||
      schedulingState.selectedDays.length > 0);

  return hasBasicData && isWithinLimit.value;
});

const scheduleInsights = computed(() => {
  if (!showPreview.value) return null;

  const totalDates = scheduledDatesPreview.value.length;
  const startDate = new Date(schedulingState.startDate);
  const endDate = new Date(schedulingState.endDate);
  const daysDiff = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  let frequency = '';
  switch (schedulingState.recurrenceType) {
    case 'daily':
      frequency = 'Todos os dias';
      break;
    case 'weekly':
      frequency = 'Semanalmente';
      break;
    case 'monthly':
      frequency = 'Mensalmente';
      break;
    case 'custom': {
      const dayNames = schedulingState.selectedDays
        .map(d => Object.values(schedulingState.dayNames)[d].slice(0, 3))
        .join(', ');
      frequency = dayNames;
      break;
    }
    default:
      break;
  }

  return {
    totalDates,
    daysDiff,
    frequency,
  };
});

const tabValidation = computed(() => ({
  configure: showPreview.value,
  preview: showPreview.value,
}));

let saveTimeout = null;
const saveSchedulingData = () => {
  if (isLoadingFromStore.value) return;

  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    const currentScheduleData = {
      startDate: schedulingState.startDate,
      endDate: schedulingState.endDate,
      fromTime: schedulingState.fromTime,
      recurrenceType: schedulingState.recurrenceType,
      selectedDays: [...(schedulingState.selectedDays || [])],
    };

    store.dispatch('campaigns/saveWhatsappNormalCampaignData', {
      schedule: currentScheduleData,
    });
  }, 300);
};

const canNavigateToTab = tabId => {
  if (tabId === 'configure') return true;
  return tabValidation.value.configure;
};

const onTabChange = tabId => {
  if (!canNavigateToTab(tabId)) return;
  saveSchedulingData();
  activeTab.value = tabId;
};

const onListDestinatario = () => {
  saveSchedulingData();
  let routeName;
  if (route.query.sourceType === 'google_sheets') {
    routeName = 'campaigns_whatsapp_app_configure_google_sheets';
  } else if (route.query.sourceType === 'database') {
    routeName = 'campaigns_whatsapp_app_configure_database';
  } else {
    routeName = 'campaigns_whatsapp_app_configure_list';
  }
  router.push({
    name: routeName,
    params: { accountId: route.params.accountId },
    query: {
      templateName: route.query.templateName || props.templateName,
      ...(route.query.sourceType && { sourceType: route.query.sourceType }),
    },
  });
};

const toggleDay = (dayIndex, checked) => {
  const index = Number(dayIndex);
  if (checked) {
    if (!schedulingState.selectedDays.includes(index)) {
      schedulingState.selectedDays.push(index);
    }
  } else {
    schedulingState.selectedDays = schedulingState.selectedDays.filter(
      d => d !== index
    );
  }
};

const formatDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString + 'T00:00:00');
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

watch(
  () => schedulingState.startDate,
  newStartDate => {
    if (newStartDate && schedulingState.endDate < newStartDate) {
      schedulingState.endDate = newStartDate;
    }
  }
);

watch(
  () => [
    schedulingState.startDate,
    schedulingState.endDate,
    schedulingState.fromTime,
    schedulingState.recurrenceType,
  ],
  () => {
    if (hasLoadedInitialData.value && !isLoadingFromStore.value) {
      saveSchedulingData();
    }
  }
);

watch(
  () => schedulingState.selectedDays,
  () => {
    if (hasLoadedInitialData.value && !isLoadingFromStore.value) {
      saveSchedulingData();
    }
  },
  { deep: true }
);

watch(
  () => campaignData.value?.schedule,
  async newSchedule => {
    if (isLoadingFromStore.value) return;

    // Store was cleared (new campaign) — reset local state
    if (!newSchedule) {
      resetState();
      hasLoadedInitialData.value = false;
      return;
    }

    isLoadingFromStore.value = true;

    try {
      await nextTick();

      if (
        newSchedule.startDate !== undefined &&
        newSchedule.startDate !== schedulingState.startDate
      ) {
        schedulingState.startDate = newSchedule.startDate;
      }
      if (
        newSchedule.endDate !== undefined &&
        newSchedule.endDate !== schedulingState.endDate
      ) {
        schedulingState.endDate = newSchedule.endDate;
      }
      if (
        newSchedule.fromTime !== undefined &&
        newSchedule.fromTime !== schedulingState.fromTime
      ) {
        schedulingState.fromTime = newSchedule.fromTime;
      }
      if (
        newSchedule.recurrenceType !== undefined &&
        newSchedule.recurrenceType !== schedulingState.recurrenceType
      ) {
        schedulingState.recurrenceType = newSchedule.recurrenceType;
      }
      if (
        newSchedule.selectedDays !== undefined &&
        Array.isArray(newSchedule.selectedDays)
      ) {
        schedulingState.selectedDays = [...newSchedule.selectedDays];
      }

      hasLoadedInitialData.value = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('❌ Erro ao carregar dados do schedule:', error);
    } finally {
      setTimeout(() => {
        isLoadingFromStore.value = false;
      }, 100);
    }
  },
  { immediate: true, deep: true }
);

const handleSubmit = async () => {
  const isValid = await v$.value.$validate();
  if (isValid) {
    const scheduledDates = generateScheduledDates();

    const scheduleData = {
      startDate: schedulingState.startDate,
      endDate: schedulingState.endDate,
      fromTime: schedulingState.fromTime,
      recurrenceType: schedulingState.recurrenceType,
      selectedDays:
        schedulingState.recurrenceType === 'custom'
          ? schedulingState.selectedDays
          : [],
      scheduledDates,
    };

    if (route.query.sourceType === 'google_sheets' || route.query.sourceType === 'database') {
      // Template-based flow (Google Sheets or Database) — save schedule into campaign data
      const currentData = store.getters['campaigns/getCampaignData'] || {};
      await store.dispatch('campaigns/saveCampaignData', {
        ...currentData,
        schedule: scheduleData,
      });
    } else {
      // Tag-based flow
      await store.dispatch('campaigns/saveWhatsappNormalCampaignData', {
        schedule: scheduleData,
      });
    }

    router.push({
      name: 'campaigns_whatsapp_app_finish',
      params: { accountId: store.state.auth.accountId },
      query: {
        templateName: props.templateName,
        ...(route.query.sourceType && { sourceType: route.query.sourceType }),
      },
    });
  }
};

onMounted(() => {
  // Reset state if no schedule data in store (new campaign)
  const sourceType = route.query.sourceType;
  const existingSchedule =
    sourceType === 'google_sheets' || sourceType === 'database'
      ? store.getters['campaigns/getCampaignData']?.schedule
      : campaignData.value?.schedule;

  if (!existingSchedule) {
    resetState();
  }

  setTimeout(() => {
    hasLoadedInitialData.value = true;
  }, 500);
});
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div
    class="border border-woot-400 dark:border-woot-400 bg-n-background min-h-[100px] p-6 w-full min-w-0 flex-1 overflow-y-auto"
  >
    <!-- Header -->
    <div class="mb-4 sm:mb-6">
      <PageHeader
        class="w-full max-w-4xl"
        :header-title="$t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.TITLE')"
        :header-content="$t('CAMPAIGN.WHATSAPP_OFFICIAL.ADD_SCHEDULE.BODY')"
      />
    </div>

    <!-- Main Content -->
    <div class="w-full">
      <Tabs
        :model-value="activeTab"
        class="w-full"
        @update:model-value="onTabChange"
      >
        <!-- Tab Navigation -->
        <TabsList class="tab-list">
          <TabsTrigger
            v-for="tab in tabs"
            :key="tab.id"
            :value="tab.id"
            :disabled="!canNavigateToTab(tab.id)"
            class="relative transition-all duration-200 text-xs sm:text-sm font-medium"
            :class="[
              !canNavigateToTab(tab.id) && 'opacity-50 cursor-not-allowed',
            ]"
          >
            <div
              class="flex items-center justify-center gap-1 sm:gap-2 min-w-0"
            >
              <span class="text-sm sm:text-base flex-shrink-0">{{
                tab.icon
              }}</span>
              <span class="hidden sm:block truncate">{{ tab.label }}</span>
              <span class="sm:hidden truncate">{{ tab.shortLabel }}</span>
              <span
                v-if="tabValidation[tab.id]"
                class="text-green-500 dark:text-green-400 ml-1 flex-shrink-0"
              >
                ✓
              </span>
            </div>
          </TabsTrigger>
        </TabsList>

        <!-- Tab Content -->
        <div class="mt-4">
          <!-- Configure Tab -->
          <TabsContent value="configure" class="space-y-4 sm:space-y-6">
            <div
              class="min-h-[475px] bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 flex flex-col justify-between"
            >
              <!-- Form Content - CONTEÚDO REAL -->
              <div class="space-y-5 flex-1 px-6 mt-6">
                <!-- Date & Time Section -->
                <div class="space-y-4">
                  <h3
                    class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2"
                  >
                    <span
                      class="w-5 h-5 bg-n-blue-9 rounded-full flex items-center justify-center"
                    >
                      <svg
                        class="w-2.5 h-2.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </span>
                    Data e Horário
                  </h3>

                  <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    <div class="space-y-2">
                      <label
                        class="block text-sm font-medium text-n-slate-11 dark:text-white"
                      >
                        Data de Início
                      </label>
                      <Input
                        v-model="schedulingState.startDate"
                        type="date"
                        :min="currentDate"
                        :message="formErrors.startDate"
                        :message-type="formErrors.startDate ? 'error' : 'info'"
                        class="w-full"
                      />
                    </div>

                    <div class="space-y-2">
                      <label
                        class="block text-sm font-medium text-n-slate-11 dark:text-white"
                      >
                        Data de Fim
                      </label>
                      <Input
                        v-model="schedulingState.endDate"
                        type="date"
                        :min="schedulingState.startDate || currentDate"
                        :message="formErrors.endDate"
                        :message-type="formErrors.endDate ? 'error' : 'info'"
                        class="w-full"
                      />
                    </div>

                    <div class="space-y-2">
                      <label
                        class="block text-sm font-medium text-n-slate-11 dark:text-white"
                      >
                        Horário
                      </label>
                      <Input
                        v-model="schedulingState.fromTime"
                        type="time"
                        :message="formErrors.fromTime"
                        :message-type="formErrors.fromTime ? 'error' : 'info'"
                        class="w-full"
                      />
                    </div>
                  </div>
                </div>

                <!-- Recurrence Section -->
                <div class="space-y-4">
                  <h3
                    class="text-sm font-medium text-n-slate-12 dark:text-white flex items-center gap-2"
                  >
                    <span
                      class="w-5 h-5 bg-n-iris-9 rounded-full flex items-center justify-center"
                    >
                      <svg
                        class="w-2.5 h-2.5 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                    </span>
                    Recorrência
                  </h3>

                  <div class="max-w-md">
                    <ComboBox
                      ref="recurrenceCombo"
                      v-model="schedulingState.recurrenceType"
                      class="outline-n-weak dark:outline-n-weak hover:outline-n-slate-6 dark:hover:outline-n-slate-6 disabled:outline-n-weak dark:disabled:outline-n-weak focus:outline-n-brand-ink dark:focus:outline-n-brand-ink block w-full reset-base text-sm outline outline-1 border-none border-0 outline-offset-[-1px] rounded-lg bg-n-alpha-black2 file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10 disabled:cursor-not-allowed disabled:opacity-50 text-n-slate-12 transition-all duration-500 ease-in-out"
                      :options="[
                        { value: 'daily', label: 'Diariamente' },
                        {
                          value: 'weekly',
                          label: 'Semanalmente (mesmo dia da semana)',
                        },
                        {
                          value: 'monthly',
                          label: 'Mensalmente (mesmo dia do mês)',
                        },
                        { value: 'custom', label: 'Personalizado' },
                      ]"
                      placeholder="Selecione a frequência"
                    />
                  </div>

                  <!-- Custom Days Selection -->
                  <div
                    v-if="schedulingState.recurrenceType === 'custom'"
                    class="space-y-4"
                  >
                    <h4 class="font-medium text-n-slate-12 dark:text-white">
                      Escolha os dias da semana
                    </h4>
                    <div class="grid grid-cols-7 gap-2">
                      <div
                        v-for="(dayName, dayIndex) in schedulingState.dayNames"
                        :key="dayIndex"
                        class="text-center"
                      >
                        <input
                          :id="'day-' + dayIndex"
                          type="checkbox"
                          :checked="
                            schedulingState.selectedDays.includes(
                              Number(dayIndex)
                            )
                          "
                          class="sr-only"
                          @change="e => toggleDay(dayIndex, e.target.checked)"
                        />
                        <label
                          :for="'day-' + dayIndex"
                          class="block w-10 h-10 rounded-xl border-2 cursor-pointer transition-colors flex items-center justify-center text-sm font-medium"
                          :class="[
                            schedulingState.selectedDays.includes(
                              Number(dayIndex)
                            )
                              ? 'bg-n-blue-9 border-n-blue-9 text-white'
                              : 'bg-n-slate-1 dark:bg-n-slate-11 border-n-slate-4 dark:border-n-slate-8 text-n-slate-11 dark:text-n-slate-3 hover:border-n-blue-6',
                          ]"
                        >
                          {{ dayName.charAt(0) }}
                        </label>
                        <span
                          class="block mt-1 text-xs text-n-slate-11 dark:text-white"
                        >
                          {{ dayName.slice(0, 3) }}
                        </span>
                      </div>
                    </div>
                    <span
                      v-if="formErrors.selectedDays || v$.selectedDays.$error"
                      class="block text-sm text-n-ruby-9"
                    >
                      {{
                        formErrors.selectedDays ||
                        v$.selectedDays.$errors[0].$message
                      }}
                    </span>
                  </div>
                </div>

                <!-- Limite de Agendamentos -->
                <div v-if="scheduleCount > 0" class="space-y-3">
                  <div
                    class="rounded-lg p-4 border"
                    :class="[
                      isWithinLimit
                        ? 'bg-woot-50 dark:bg-woot-900 border-woot-400 dark:border-woot-400'
                        : 'bg-red-50 dark:bg-red-900 border-red-200 dark:border-red-800',
                    ]"
                  >
                    <div class="flex items-center justify-between mb-2">
                      <h4
                        class="font-medium flex items-center gap-2"
                        :class="[
                          isWithinLimit
                            ? 'text-blue-900 dark:text-blue-100'
                            : 'text-red-900 dark:text-red-100',
                        ]"
                      >
                        <svg
                          class="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                          />
                        </svg>
                        Agendamentos
                      </h4>
                      <span
                        class="px-3 py-1 rounded-full text-sm font-medium"
                        :class="[
                          isWithinLimit
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
                        ]"
                      >
                        {{ scheduleCount }} / {{ MAX_SCHEDULES }}
                      </span>
                    </div>

                    <div
                      class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-3"
                    >
                      <div
                        class="h-2 rounded-full transition-all duration-500"
                        :class="[isWithinLimit ? 'bg-blue-500' : 'bg-red-500']"
                        :style="{
                          width:
                            Math.min(
                              100,
                              (scheduleCount / MAX_SCHEDULES) * 100
                            ) + '%',
                        }"
                      />
                    </div>

                    <p
                      class="text-sm"
                      :class="[
                        isWithinLimit
                          ? 'text-blue-700 dark:text-blue-300'
                          : 'text-red-700 dark:text-red-300',
                      ]"
                    >
                      <span v-if="isWithinLimit">
                        Esta configuração gerará
                        <strong>{{ scheduleCount }} agendamentos</strong>.
                        Restam {{ MAX_SCHEDULES - scheduleCount }} agendamentos
                        disponíveis.
                      </span>
                      <span v-else>
                        <strong>Limite excedido!</strong> Esta configuração
                        geraria {{ scheduleCount }} agendamentos, mas o máximo
                        permitido é {{ MAX_SCHEDULES }}.
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <!-- Modern Action Bar - SEMPRE NA PARTE INFERIOR -->
              <div class="modern-action-bar mt-6">
                <div class="action-bar-background" />

                <!-- Back Button -->
                <div class="back-section px-8">
                  <button
                    type="button"
                    class="modern-button back-button"
                    @click="onListDestinatario()"
                  >
                    <div class="button-shine" />
                    <ArrowLeft class="button-icon" />
                    <span class="hidden sm:inline">Retornar</span>
                    <span class="sm:hidden">Retornar</span>
                  </button>
                </div>

                <!-- Center Space -->
                <div class="center-actions" />

                <!-- Primary Actions -->
                <div class="primary-actions px-8">
                  <button
                    type="button"
                    :disabled="!showPreview"
                    class="modern-button primary-button"
                    @click="onTabChange('preview')"
                  >
                    <div class="primary-gradient" />
                    <div class="primary-glow" />
                    <div class="button-content">
                      <span class="button-text">
                        <span class="hidden sm:inline">Visualizar Agenda</span>
                        <span class="sm:hidden">Ir Agenda</span>
                        <ArrowRight class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>

          <!-- Preview Tab -->
          <TabsContent value="preview">
            <div
              class="min-h-[475px] bg-n-solid-1 rounded-lg border border-woot-400 dark:border-woot-400 flex flex-col justify-between"
            >
              <!-- Preview Content -->
              <div
                v-if="showPreview && scheduleInsights"
                class="space-y-5 flex-1 px-6 mt-6"
              >
                <!-- Resumo da Campanha -->
                <div
                  class="bg-gradient-to-br from-n-blue-1 to-n-blue-2 dark:from-n-blue-12 dark:to-n-blue-11 rounded-xl p-4 border border-n-blue-4 dark:border-n-blue-9"
                >
                  <div class="flex items-start justify-between mb-4">
                    <div>
                      <h3
                        class="text-lg font-semibold text-n-blue-11 dark:text-n-blue-2 mb-1"
                      >
                        Resumo do Agendamento
                      </h3>
                      <p class="text-sm text-n-blue-10 dark:text-n-blue-4">
                        De {{ formatDate(schedulingState.startDate) }} até
                        {{ formatDate(schedulingState.endDate) }}
                      </p>
                    </div>
                    <div
                      class="w-12 h-12 bg-n-blue-9 dark:bg-n-blue-6 rounded-full flex items-center justify-center"
                    >
                      <svg
                        class="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                  </div>

                  <div
                    class="text-2xl font-bold text-n-blue-11 dark:text-n-blue-2 mb-1"
                  >
                    {{ schedulingState.fromTime }}
                  </div>
                  <div class="text-sm text-n-blue-10 dark:text-n-blue-4">
                    Horário de envio configurado
                  </div>
                </div>

                <!-- Cards de Estatísticas -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <!-- Total de Envios -->
                  <div
                    class="group bg-gradient-to-br from-white to-n-slate-1 dark:from-n-slate-12 dark:to-n-slate-11 rounded-xl p-6 border border-n-slate-4 dark:border-n-slate-8 hover:shadow-lg hover:border-n-blue-6 dark:hover:border-n-blue-8 transition-all duration-300"
                  >
                    <div class="flex items-center justify-between mb-3">
                      <div
                        class="w-10 h-10 bg-gradient-to-br from-n-blue-9 to-n-blue-11 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                      >
                        <svg
                          class="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                          />
                        </svg>
                      </div>
                      <div class="text-right">
                        <div
                          class="text-3xl font-bold text-n-blue-11 dark:text-n-blue-3 leading-none"
                        >
                          {{ scheduleInsights.totalDates }}
                        </div>
                        <div
                          class="text-xs text-n-blue-9 dark:text-n-blue-6 font-medium mt-1"
                        >
                          ENVIOS
                        </div>
                      </div>
                    </div>
                    <div class="text-sm text-n-slate-11 dark:text-n-slate-4">
                      Total de mensagens programadas
                    </div>
                  </div>

                  <!-- Duração da Campanha -->
                  <div
                    class="group bg-gradient-to-br from-white to-n-slate-1 dark:from-n-slate-12 dark:to-n-slate-11 rounded-xl p-6 border border-n-slate-4 dark:border-n-slate-8 hover:shadow-lg hover:border-n-iris-6 dark:hover:border-n-iris-8 transition-all duration-300"
                  >
                    <div class="flex items-center justify-between mb-3">
                      <div
                        class="w-10 h-10 bg-gradient-to-br from-n-iris-9 to-n-iris-11 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                      >
                        <svg
                          class="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div class="text-right">
                        <div
                          class="text-3xl font-bold text-n-iris-11 dark:text-n-iris-3 leading-none"
                        >
                          {{ scheduleInsights.daysDiff }}
                        </div>
                        <div
                          class="text-xs text-n-iris-9 dark:text-n-iris-6 font-medium mt-1"
                        >
                          DIAS
                        </div>
                      </div>
                    </div>
                    <div class="text-sm text-n-slate-11 dark:text-n-slate-4">
                      Duração total da campanha
                    </div>
                  </div>

                  <!-- Frequência -->
                  <div
                    class="group bg-gradient-to-br from-white to-n-slate-1 dark:from-n-slate-12 dark:to-n-slate-11 rounded-xl p-6 border border-n-slate-4 dark:border-n-slate-8 hover:shadow-lg hover:border-n-teal-6 dark:hover:border-n-teal-8 transition-all duration-300"
                  >
                    <div class="flex items-center justify-between mb-3">
                      <div
                        class="w-10 h-10 bg-gradient-to-br from-n-teal-9 to-n-teal-11 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300"
                      >
                        <svg
                          class="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                      </div>
                      <div class="text-right flex-1 min-w-0">
                        <div
                          class="text-lg font-bold text-n-teal-11 dark:text-n-teal-3 leading-tight truncate"
                        >
                          {{ scheduleInsights.frequency }}
                        </div>
                        <div
                          class="text-xs text-n-teal-9 dark:text-n-teal-6 font-medium mt-1"
                        >
                          FREQUÊNCIA
                        </div>
                      </div>
                    </div>
                    <div class="text-sm text-n-slate-11 dark:text-n-slate-4">
                      Padrão de repetição
                    </div>
                  </div>
                </div>
              </div>

              <!-- Modern Action Bar -->
              <div class="modern-action-bar mt-6">
                <div class="action-bar-background" />

                <!-- Back Button -->
                <div class="back-section px-8">
                  <button
                    type="button"
                    class="modern-button back-button"
                    @click="onTabChange('configure')"
                  >
                    <div class="button-shine" />
                    <ArrowLeft class="button-icon" />
                    <span class="hidden sm:inline">
                      Voltar para Configuração
                    </span>
                    <span class="sm:hidden">Voltar</span>
                  </button>
                </div>

                <!-- Center Space -->
                <div class="center-actions" />

                <!-- Primary Actions -->
                <div class="primary-actions px-8">
                  <button
                    type="button"
                    :disabled="!showPreview"
                    class="modern-button primary-button"
                    @click="handleSubmit"
                  >
                    <div class="primary-gradient" />
                    <div class="primary-glow" />
                    <div class="button-content">
                      <span class="button-text">
                        <Check class="button-icon mr-2" />
                        Confirmar e Agendar Campanha
                        <Sparkles class="button-icon ml-2" />
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  </div>
</template>

<style scoped>
* {
  box-sizing: border-box;
}

/* Tab Styles (existing) */
.tab-list {
  @apply grid w-full grid-cols-2 p-1 rounded-lg;
  @apply bg-white dark:bg-slate-900;
  @apply border border-woot-400 dark:border-woot-400;
}

.min-w-0 {
  min-width: 0;
}

:deep(.tabs-trigger) {
  transition: all 0.2s ease-in-out;
}

:deep(.tabs-trigger[data-state='active']) {
  background: white;
  border: 1px solid rgb(34 197 94);
  color: rgb(21 128 61);
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1);
}

:deep(.tabs-trigger[data-state='active']) .dark {
  background: rgb(15 23 42);
  color: rgb(74 222 128);
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: rgb(var(--slate-3));
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: rgb(var(--slate-6));
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: rgb(var(--slate-8));
}

/* Modern Action Bar Styles (matching TabContentWrapper.vue) */
.modern-action-bar {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 0;
  margin-top: 24px;
  background: linear-gradient(
    135deg,
    rgb(var(--solid-2) / 0.9),
    rgb(var(--solid-3) / 0.8)
  );
  border-top: 1px solid rgb(var(--teal-6) / 0.3);
  backdrop-filter: blur(10px);
  border-radius: 0 0 12px 12px;
}

.action-bar-background {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      circle at 0% 50%,
      rgb(var(--teal-4) / 0.1),
      transparent 50%
    ),
    radial-gradient(
      circle at 100% 50%,
      rgb(var(--iris-4) / 0.1),
      transparent 50%
    );
  pointer-events: none;
}

.back-section {
  flex-shrink: 0;
}

.center-actions {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.primary-actions {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
}

/* Base Button Styles */
.modern-button {
  position: relative;
  height: 48px;
  padding: 0 24px;
  border-radius: 12px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-width: 140px;
  overflow: hidden;
}

.modern-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none !important;
}

/* Button Shine Effect */
.button-shine {
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    45deg,
    transparent 30%,
    rgba(255, 255, 255, 0.3) 50%,
    transparent 70%
  );
  transform: translateX(-100%);
  transition: transform 0.6s;
  pointer-events: none;
}

.modern-button:hover .button-shine {
  transform: translateX(100%);
}

/* Back Button */
.back-button {
  background: linear-gradient(135deg, rgb(var(--solid-2)), rgb(var(--solid-3)));
  color: rgb(var(--slate-11));
  border: 1px solid rgb(var(--border-weak));
}

.back-button:hover {
  background: linear-gradient(135deg, rgb(var(--solid-3)), rgb(var(--slate-3)));
  border-color: rgb(var(--slate-6) / 0.6);
  transform: translateX(-2px) scale(1.02);
  box-shadow: 0 8px 25px rgb(var(--slate-4) / 0.2);
}

/* Preview Button */
.preview-button {
  background: linear-gradient(135deg, transparent, rgb(var(--iris-2) / 0.3));
  color: rgb(var(--slate-11));
  border: 2px solid rgb(var(--iris-6) / 0.5);
  position: relative;
}

.preview-glow {
  position: absolute;
  inset: -2px;
  background: linear-gradient(
    135deg,
    rgb(var(--iris-5) / 0.5),
    rgb(var(--blue-5) / 0.5)
  );
  border-radius: 12px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(8px);
}

.preview-button:hover {
  background: linear-gradient(
    135deg,
    rgb(var(--iris-2) / 0.5),
    rgb(var(--blue-2) / 0.5)
  );
  border-color: rgb(var(--iris-7) / 0.8);
  color: rgb(var(--iris-11));
  transform: scale(1.05);
}

.preview-button:hover .preview-glow {
  opacity: 1;
}

/* Primary Button */
.primary-button {
  position: relative;
  color: white;
  font-weight: 700;
  border: none;
}

.primary-gradient {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgb(var(--teal-9)),
    rgb(var(--teal-10)),
    rgb(var(--teal-11))
  );
  border-radius: 12px;
  transition: all 0.3s;
}

.primary-glow {
  position: absolute;
  inset: -4px;
  background: linear-gradient(
    135deg,
    rgb(var(--teal-6) / 0.6),
    rgb(var(--teal-8) / 0.4)
  );
  border-radius: 16px;
  opacity: 0;
  transition: opacity 0.3s;
  z-index: -1;
  filter: blur(12px);
}

.button-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.primary-button:hover {
  transform: translateY(-2px) scale(1.05);
}

.primary-button:hover .primary-gradient {
  background: linear-gradient(
    135deg,
    rgb(var(--teal-10)),
    rgb(var(--teal-11)),
    rgb(var(--teal-12))
  );
  animation: gradient-shift 2s ease-in-out infinite;
}

.primary-button:hover .primary-glow {
  opacity: 1;
}

@keyframes gradient-shift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.primary-button:active {
  transform: translateY(0) scale(1.02);
}

/* Button Elements */
.button-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.button-text {
  display: flex;
  align-items: center;
  gap: 8px;
}

.back-button:hover .button-icon {
  transform: translateX(-2px);
}

.primary-button:hover .button-icon {
  transform: translateX(2px) scale(1.1);
}

.preview-button:hover .button-icon {
  transform: scale(1.1);
}

/* Responsive */
@media (max-width: 640px) {
  .modern-action-bar {
    flex-direction: column;
    gap: 12px;
    padding: 20px;
  }

  .back-section,
  .primary-actions {
    width: 100%;
  }

  .primary-actions {
    flex-direction: column;
  }

  .modern-button {
    min-width: 100%;
    height: 52px;
    font-size: 16px;
  }
}

/* Dark Mode */
@media (prefers-color-scheme: dark) {
  .modern-action-bar {
    background: linear-gradient(
      135deg,
      rgb(var(--solid-2) / 0.8),
      rgb(var(--solid-3) / 0.7)
    );
    border-top-color: rgb(var(--teal-7) / 0.4);
  }

  .back-button {
    color: rgb(var(--slate-9));
  }

  .preview-button {
    color: rgb(var(--slate-9));
  }

  .preview-button:hover {
    color: rgb(var(--iris-9));
  }
}
</style>
